import React, { useState, useRef, useEffect } from 'react';

import defaultUserImage from '../../assets/img/not-found-images/no-user.jpg';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    fallbackSrc?: string;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ src, fallbackSrc = defaultUserImage, alt = 'Image', className = '', width, height, ...props }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        // Reset loading state when src changes
        setIsLoading(true);
    }, [src]);

    const handleLoad = () => {
        if (imgRef.current) {
            setDimensions({
                width: imgRef.current.offsetWidth,
                height: imgRef.current.offsetHeight,
            });

            requestAnimationFrame(() => {
                setIsLoading(false);
            });
        }
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = fallbackSrc;
        setIsLoading(false);
    };

    return (
        <div className="relative block w-full min-w-[24px] min-h-[24px]" style={{ minWidth: width || '24px', minHeight: height || '24px' }}>
            <div
                className={`absolute inset-0 bg-gray-200 rounded-full ${isLoading ? 'opacity-100' : 'opacity-0'}`}
                style={{
                    transition: 'opacity 0.2s ease-in-out',
                    width: dimensions.width || '24px',
                    height: dimensions.height || '24px',
                }}
            />
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                onLoad={handleLoad}
                onError={handleError}
                className={`${className} relative`}
                style={{
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                    minWidth: height || '24px',
                    minHeight: width || '24px',
                    objectFit: 'contain',
                }}
                {...props}
            />
        </div>
    );
};

export default ImageWithFallback;
